/**
 * Shared-element handoff for the chat composer.
 *
 * The redesign uses one composer in two placements: centred under the hero on
 * `/chat`, and docked at the bottom of a thread on `/chat/:conversationId`.
 * Those are separate routes, so the DOM node itself cannot survive the
 * transition. Instead the hero records where its composer sat at submit time
 * and the docked composer replays the trip on mount (a FLIP animation), which
 * reads as the same component travelling down into its dock.
 *
 * The origin is one-shot: whoever reads it clears it, so a stale rect can never
 * animate a thread the user opened from the history list instead.
 */

const MAX_AGE_MS = 8000;

let pending: { rect: DOMRect; at: number } | null = null;

/** Record where the hero composer sits, just before the send that navigates away. */
export const captureComposerOrigin = (element: HTMLElement | null): void => {
  if (!element) return;
  pending = { rect: element.getBoundingClientRect(), at: Date.now() };
};

/**
 * Take the recorded origin, if one is still fresh. Returns `null` when the
 * thread was opened some other way (history click, reload, deep link).
 */
export const consumeComposerOrigin = (): DOMRect | null => {
  const handoff = pending;
  pending = null;

  if (!handoff) return null;

  return Date.now() - handoff.at > MAX_AGE_MS ? null : handoff.rect;
};

/** Drop a recorded origin — used when the send that would have consumed it fails. */
export const clearComposerOrigin = (): void => {
  pending = null;
};
